/**
 * 台本取り込みシナリオの取り込みレポートを、確認対象の更新後に取り直す
 * (docs/features/scenario-script-import-plan.md §6.6.7)。
 *
 * BEは確認対象 (シーン本文・話者・尺・順序・作成・削除、登場人物、motion-v2変換) の更新で自動照合を失効させ、
 * 利用者確認の有効性もハッシュで判定する。FEはシナリオ APIラッパ (features/scenarios/api.ts) の成功通知
 * (importReportRefresh.ts) を受けて GET /import-report を呼び、generation_metadata.import_report を
 * lastResult にマージする。取り込み由来 (origin='imported') でないシナリオでは何もしない。
 * 再取得の失敗はログに留め、本体の更新成功は妨げない。
 */
import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { getImportReport } from '../../../../features/scenarios/api';
import { subscribeImportContentChanged, type ImportContentChange } from '../../../../features/scenarios/importReportRefresh';
import type { ImportReport } from '../../../../features/scenarios/types';
import { isImportedScenario, mergeImportReportIntoScenarios } from '../scriptImport';
import type { ScenarioResponse } from '../types';

type SetLastResult = Dispatch<SetStateAction<ScenarioResponse | null>>;

/** 最新の import_report を取得して lastResult の該当シナリオにマージする。失敗時は null (ログのみ)。 */
export async function refreshImportReport(scenarioId: string, setLastResult: SetLastResult): Promise<ImportReport | null> {
  try {
    const report = await getImportReport(scenarioId);
    if (!report || typeof report !== 'object') return null;
    setLastResult((prev) => (prev
      ? { ...prev, scenarios: mergeImportReportIntoScenarios(prev.scenarios, scenarioId, report) }
      : prev));
    return report;
  } catch (err) {
    console.warn('[importReport] 取り込みレポートの再取得に失敗しました', err);
    return null;
  }
}

export function useImportReportAutoRefresh(lastResult: ScenarioResponse | null, setLastResult: SetLastResult): void {
  const resultRef = useRef(lastResult);
  // シーンIDだけの API (delete / swap) からシナリオを引くための対応表。削除直後に state から消えても引けるよう消さない
  const sceneToScenarioRef = useRef(new Map<string, string>());

  useEffect(() => {
    resultRef.current = lastResult;
    for (const s of lastResult?.scenarios ?? []) {
      for (const scene of s.scenes ?? []) {
        if (scene?.id) sceneToScenarioRef.current.set(scene.id, s.id);
      }
    }
  }, [lastResult]);

  useEffect(() => {
    let disposed = false;
    // 同じシナリオへの連続更新 (尺の連続編集等) は 1本ずつ流し、実行中に来た更新は終了後にもう1回だけ取り直す
    const inflight = new Map<string, { dirty: boolean }>();
    const update: SetLastResult = (action) => { if (!disposed) setLastResult(action); };

    const run = async (scenarioId: string) => {
      const existing = inflight.get(scenarioId);
      if (existing) { existing.dirty = true; return; }
      const state = { dirty: false };
      inflight.set(scenarioId, state);
      try {
        do {
          state.dirty = false;
          await refreshImportReport(scenarioId, update);
        } while (state.dirty && !disposed);
      } finally {
        inflight.delete(scenarioId);
      }
    };

    const resolveScenarioId = (change: ImportContentChange): string | null => {
      if (change.scenarioId) return change.scenarioId;
      for (const sceneId of change.sceneIds ?? []) {
        const found = sceneToScenarioRef.current.get(sceneId);
        if (found) return found;
      }
      return null;
    };

    const unsubscribe = subscribeImportContentChanged((change) => {
      const scenarioId = resolveScenarioId(change);
      if (!scenarioId) return;
      const scenario = resultRef.current?.scenarios.find((s) => s.id === scenarioId);
      if (!scenario || !isImportedScenario(scenario.generation_metadata)) return;
      void run(scenarioId);
    });
    return () => { disposed = true; unsubscribe(); };
  }, [setLastResult]);
}
